import type { ComponentProps } from "react";
import { Input, Label } from "@shared/components";
import { cn } from "@shared/lib/utils";

interface FormFieldProps extends ComponentProps<typeof Input> {
  id: string;
  label: string;
  /** Already-translated message; renders the field in its error state. */
  error?: string;
  hint?: string;
}

/** Label + DS `Input` + a single hint-or-error line, wired for a11y. */
export function FormField({
  id,
  label,
  error,
  hint,
  className,
  ...inputProps
}: FormFieldProps) {
  const describedBy = error ? `${id}-error` : hint ? `${id}-hint` : undefined;

  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        aria-invalid={!!error}
        aria-describedby={describedBy}
        className={cn(
          error && "border-destructive focus-visible:ring-destructive",
          className
        )}
        {...inputProps}
      />
      {error ? (
        <p id={`${id}-error`} className="text-[13px] text-destructive">
          {error}
        </p>
      ) : hint ? (
        <p id={`${id}-hint`} className="text-[13px] text-muted-foreground">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
